import localforage from "localforage";
import {queryOptions, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useSyncExternalStore
} from "react";
import {Listener, listenerListAdd, Unlistener} from "../util/arutil.tsx";
import {useBomb} from "../bomb/bomb.tsx";

const lfAuthtokenKey = "kingbasura.authtoken";
const lfAutologinKey = "kingbasura.autologin";
export const LF_DEBUG_KEY = "kingbasura.debug";
const ZBASE32_ALPHABET = "ybndrfg8ejkmcpqxot1uwisza345h769";
const OUTLET_SLUG_BYTES = 10;
export const errUnauthenticated = new Error("unauthenticated");

// export interface AuthServiceProps {
//     wantsAutologin?: boolean;
//     authtoken?: string;
// }

//
// export type Watcher<T> = (v: T) => void;
// export type TransitionWatcher<T> = Watcher<Transition<T>>;
// export type ConnStatTransitionWatcher = TransitionWatcher<SetlistConnectionStatus>;
// export type AuthTokenTransitionWatcher = TransitionWatcher<string | null>;
// export type Unsubscriber = () => void;

export function z32Stringify(input: Uint8Array): string {
    const length = input.byteLength;
    const view = new Uint8Array(input);

    let bits = 0;
    let value = 0;
    let rv = "";

    for (let i = 0; i < length; i++) {
        value = (value << 8) | (view[i] ?? 0);
        bits += 8;

        while (bits >= 5) {
            rv += ZBASE32_ALPHABET[(value >>> (bits - 5)) & 31] ?? "?";
            bits -= 5;
        }
    }

    if (bits > 0) {
        rv += ZBASE32_ALPHABET[(value << (5 - bits)) & 31] ?? "?";
    }

    return rv;
}

export function makeOutletSlug(): string {
    const raw = new Uint8Array(OUTLET_SLUG_BYTES);
    crypto.getRandomValues(raw);
    const rv = z32Stringify(raw);
    return rv;
}

export interface Authenticator {
    getJwt: () => string | null;
    setJwt: (njwt: string | null) => void;
    listenJwt: (listener: Listener<string | null>) => Unlistener;
    getOutletSlug: () => string;
    changeOutletSlug: () => void;
    getAuthorizationHeader: () => { authorization: string } | undefined;
    performLogout: () => Promise<unknown>;
}

const AuthContext = createContext<Authenticator>({} as Authenticator);

interface AuthenticatorState {
    jwt: string | null;
    jwtListeners: Listener<string | null>[];
    outletSlug: string;
}

export function AuthenticatorProvider({children}: PropsWithChildren) {
    const tqc = useQueryClient();
    const stateRef = useRef<AuthenticatorState>({
        jwt: null,
        jwtListeners: [],
        outletSlug: makeOutletSlug()
    });
    const getAuthorizationHeader = useCallback(() => {
        const rv = stateRef.current.jwt
            ? {"authorization": `Bearer ${stateRef.current.outletSlug} ${stateRef.current.jwt}`}
            : undefined;
        return rv;
    }, []);

    const getJwt = useCallback(() => {
        return stateRef.current.jwt;
    }, []);

    const setJwt = useCallback((next: string | null) => {
        if (next !== stateRef.current.jwt) {
            stateRef.current.jwt = next;
            stateRef.current.jwtListeners.forEach(listener => {
                listener(next);
            });
        }
    }, []);

    const listenJwt = useCallback((listener: Listener<string | null>) => {
        return listenerListAdd(stateRef.current.jwtListeners, listener);
    }, []);

    const getOutletSlug = useCallback(() => {
        return stateRef.current.outletSlug;
    }, []);

    const changeOutletSlug = useCallback(() => {
        stateRef.current.outletSlug = makeOutletSlug();
    }, []);

    const performLogout = useCallback(() => {
        return localforage.removeItem(lfAuthtokenKey)
            .then(() => {
                tqc.clear();
                setJwt(null);
                changeOutletSlug();
            });
    }, [changeOutletSlug, setJwt, tqc]);

    const svc = useMemo(() => {
        const rv: Authenticator = {
            getJwt, setJwt, listenJwt,
            getOutletSlug, changeOutletSlug,
            getAuthorizationHeader, performLogout,
        };
        return rv;
    }, [changeOutletSlug, getAuthorizationHeader, getJwt, getOutletSlug,
        listenJwt, performLogout, setJwt]);

    return <AuthContext value={svc}>{children}</AuthContext>;
}

export function useAuthenticator() {
    const rv = useContext(AuthContext);
    return rv;
}

// export class AuthService {
//     constructor() {
//         this._authtoken = null;
//     }
//
//     public isAuthenticated(): boolean {
//         return !!this._authtoken;
//     }
//
//     public getAuthenticationToken() {
//         return this._authtoken;
//     }
//
//     public setAuthenticationToken(token: string | null) {
//         const trans: Transition<string | null> = {
//             prev: this._authtoken ?? null,
//             next: token,
//         };
//         this._authtoken = token;
//         if (token) {
//             void localforage.setItem(lfAuthtokenKey, this._authtoken);
//         } else {
//             void localforage.removeItem(lfAuthtokenKey);
//         }
//         this.notifyAuthTokenWatchers(trans);
//     }
//
//     public getOutletSlug(): string {
//         if (!this._outletSlug) {
//             this._outletSlug = makeOutletSlug();
//         }
//         return this._outletSlug;
//     }
//
//     public clearOutletSlug() {
//         this._outletSlug = null;
//     }
//
//     public setWantsAutologin(wants: boolean) {
//         return localforage.setItem(lfAutologinKey, wants)
//             .then(() => INVALIDATE_SINGLE({queryKey: autologinTqk}));
//     }
//
//     public performLogin(lreq: LoginRequest): Promise<LoginResponse> {
//         // this is important to prevent old 401s due to deauthenticated attempts
//         // from throwing errors on next login
//         GLOBAL_QUERY_CLIENT.clear();
//
//         const endpoint = `${import.meta.env.VITE_MOTHERSHIP_BASE}/login`;
//         const rv$ = fetch(endpoint, {
//             method: "POST",
//             headers: new Headers({"Content-Type": "application/json"}),
//             body: JSON.stringify(lreq),
//         })
//             .then(rsp => {
//                 if (rsp.status !== 200) {
//                     return {error: new Error(rsp.statusText)};
//                 } else {
//                     return rsp.json() as LoginResponse
//                 }
//             });
//         void rv$.then((lrsp) => {
//             const token = lrsp.authtoken ? lrsp.authtoken : null;
//             this.setAuthenticationToken(token);
//         })
//             .catch(() => {
//                 this.setAuthenticationToken(null);
//             });
//
//         return rv$;
//     }
//
//     // public performAutologin(): Promise<unknown> {
//     //     return localforage.getItem(lfAuthtokenKey)
//     //         .then((atoken) => {
//     //             this._authtoken = atoken as string | null;
//     //             if (this._authtoken) {
//     //                 GLOBAL_QUERY_CLIENT.clear();
//     //                 return this.establishSetlistFeed();
//     //             }
//     //         })
//     //         .catch((xc) => {
//     //             console.log(`autologin rejected ${xc}`);
//     //             this.setAuthenticationToken(null);
//     //         });
//     // }
//
//     public performLogout(): Promise<void> {
//         void GLOBAL_MOTHERSHIP.logout();
//         GLOBAL_QUERY_CLIENT.clear();
//         this.setAuthenticationToken(null);
//         this.clearOutletSlug();
//         return Promise.resolve();
//     }
//
//     public getSetlistFeedConnectionStatus() {
//         return this._setlistFeedConnectionStatus;
//     }
//
//     public watchSetlistFeedConnectionStatus(watcher: ConnStatTransitionWatcher): Unsubscriber {
//         if (!this._setlistFeedConnectionWatchers.includes(watcher)) {
//             this._setlistFeedConnectionWatchers.push(watcher);
//         }
//         const rv = () => {
//             const ix = this._setlistFeedConnectionWatchers.indexOf(watcher);
//             if (ix >= 0) {
//                 this._setlistFeedConnectionWatchers.splice(ix, 1);
//             }
//         };
//         return rv;
//     }
//
//     public watchAuthToken(watcher: AuthTokenTransitionWatcher): Unsubscriber {
//         if (!this._authtokenWatchers.includes(watcher)) {
//             this._authtokenWatchers.push(watcher);
//         }
//         const rv = () => {
//             const ix = this._authtokenWatchers.indexOf(watcher);
//             if (ix >= 0) {
//                 this._authtokenWatchers.splice(ix, 1);
//             }
//         };
//         return rv;
//     }
//
//     public getSetlistAbortSignal(): AbortSignal {
//         if (!this._setlistFeedAbortController || this._setlistFeedAbortController.signal.aborted) {
//             this._setlistFeedAbortController = new AbortController();
//         }
//         return this._setlistFeedAbortController.signal;
//     }
//
//     public isWatchingRawSetlistFeed(): boolean {
//         return !!this._rawSetlistWatcher;
//     }
//
//     public watchRawSetlistFeed(watcher: Watcher<WiredSetlistPacket>): Unsubscriber {
//         if (this._rawSetlistWatcher) {
//             throw new Error("already watching raw setlist feed");
//         }
//         this._rawSetlistWatcher = watcher;
//         const rv = () => {
//             this._rawSetlistWatcher = undefined;
//         };
//         return rv;
//     }
//
//     // a full reset is like a logout and an autologin, but
//     // does not reroute
//
//     public performFullReset() {
//         const atok = this._authtoken;
//         if (atok) {
//             this.teardownOnDeauthenticate({prev: atok, next: null});
//             this.clearOutletSlug();
//             GLOBAL_QUERY_CLIENT.clear();
//             this.establishOnAuthenticate({prev: null, next: atok});
//             resetGlobalDispatchMesh();
//         }
//     }
//
//     private setSetlistFeedConnectionStatus(nv: SetlistConnectionStatus) {
//         if (nv !== this._setlistFeedConnectionStatus) {
//             const trans: Transition<SetlistConnectionStatus> = {
//                 prev: this._setlistFeedConnectionStatus,
//                 next: nv,
//             };
//             this._setlistFeedConnectionStatus = nv;
//             this.notifyConnStatWatchers(trans);
//         }
//     }
//
//     private callSetlistServer(): Promise<unknown> {
//         if (this._setlistFeedConnectionStatus !== SetlistConnectionDown
//             && this._setlistFeedConnectionStatus !== SetlistConnectionDead
//             && !isSetlistConnectionErrored(this._setlistFeedConnectionStatus)) {
//             return Promise.resolve();
//         }
//
//         console.log("initiating setlist call");
//         this.setSetlistFeedConnectionStatus(SetlistConnectionCalling);
//         const p = new Promise((resolve, reject) => {
//             void GLOBAL_MOTHERSHIP.setlists({
//                 onOpen: () => {
//                     console.log("setlist connection opened");
//                     this.setSetlistFeedConnectionStatus(SetlistConnectionUp);
//                     resolve(SetlistConnectionUp);
//                 },
//                 onClose: () => {
//                     console.log("setlist connection closed");
//                     this.setSetlistFeedConnectionStatus(SetlistConnectionDown);
//                 },
//                 onError: (err) => {
//                     if (isAbortError(err)) {
//                         this.setSetlistFeedConnectionStatus(SetlistConnectionDown);
//                     } else {
//                         console.log(`unexpected webrpc error ${err.message}`);
//                         const stat = {status: "error", error: err};
//                         this.setSetlistFeedConnectionStatus(stat);
//                         if (this._setlistFeedAbortController) {
//                             this._setlistFeedAbortController.abort(err);
//                         }
//                         reject(err);
//                     }
//                 },
//                 onMessage: ({packet}: { packet: WiredSetlistPacket }) => {
//                     console.log("setlist packet received " + JSON.stringify(packet));
//                     // if (packet.path) {
//                     updateCachedSetlists(packet);
//                     //}
//                     if (this._rawSetlistWatcher) {
//                         this._rawSetlistWatcher(packet);
//                     }
//                 },
//                 signal: this.getSetlistAbortSignal(),
//             });
//         });
//         return p;
//     }
//
//     private static defaultBackoffOpts = {
//         numOfAttempts: 8,
//         startingDelay: 300,
//     };
//
//     public establishSetlistFeed(inOpts?: BackoffOptions) {
//         const opts: BackoffOptions = {
//             ...AuthService.defaultBackoffOpts,
//             ...inOpts,
//         };
//         opts.retry = (e, attemptNumber) => {
//             if (attemptNumber >= (opts.numOfAttempts ?? 0)) {
//                 console.log(`abandoning setlist feed ${JSON.stringify(e)}`);
//                 this.setSetlistFeedConnectionStatus(SetlistConnectionDead);
//                 return false;
//             }
//             console.log(`retrying setlist feed ${JSON.stringify(e)}`);
//             return true;
//         };
//
//         if (!this._authtoken) {
//             throw (new Error("not authenticated"));
//         }
//         if (this._setlistFeedConnectionStatus === SetlistConnectionDown
//             || this._setlistFeedConnectionStatus === SetlistConnectionDead) {
//             backOff(() => this.callSetlistServer(), opts)
//                 .catch((xc: unknown) => {
//                     console.log(`backoff failed ${JSON.stringify(xc)}`);
//                     this.setSetlistFeedConnectionStatus(SetlistConnectionDead);
//                 });
//         }
//     }
//
//     public teardownSetlistFeed(reason: any): void {
//         if (this._setlistFeedConnectionStatus === SetlistConnectionUp
//             || this._setlistFeedConnectionStatus === SetlistConnectionCalling) {
//             if (this._setlistFeedAbortController) {
//                 this.setSetlistFeedConnectionStatus(SetlistConnectionClosing);
//                 this._setlistFeedAbortController.abort(reason);
//                 this.setSetlistFeedConnectionStatus(SetlistConnectionDown);
//             } else {
//                 throw new Error("teardownSetlistFeed called without abort controller signal ever being asked for");
//             }
//         }
//     }
//
//     private notifyAuthTokenWatchers(trans: Transition<string | null>): void {
//         this._authtokenWatchers.forEach((watcher) => {
//             watcher(trans);
//         });
//     }
//
//     private notifyConnStatWatchers(trans: Transition<SetlistConnectionStatus>): void {
//         this._setlistFeedConnectionWatchers.forEach((watcher) => {
//             watcher(trans);
//         });
//     }
//
//     private teardownOnDeauthenticate(trans: Transition<string | null>) {
//         if (!trans.next) {
//             this.teardownSetlistFeed("deauthenticated");
//             this._rawSetlistWatcher = undefined;
//         }
//     }
//
//     private establishOnAuthenticate(trans: Transition<string | null>) {
//         if (!trans.prev && !!trans.next) {
//             this.establishSetlistFeed();
//         }
//     }
//
//     private _authtoken?: string | null = null;
//     private _authtokenWatchers: AuthTokenTransitionWatcher[] = [
//         this.teardownOnDeauthenticate.bind(this),
//         this.establishOnAuthenticate.bind(this),
//     ];
//     private _outletSlug: string | null = null;
//     private _setlistFeedConnectionStatus: SetlistConnectionStatus = SetlistConnectionDown;
//     private _setlistFeedAbortController: AbortController | null = null;
//
//     private reestablishAfterDropped(trans: Transition<SetlistConnectionStatus>) {
//         if (isSetlistConnectionErrored(trans.next)) {
//             this._setlistFeedConnectionStatus = SetlistConnectionDown;
//         }
//
//         if (trans.prev === SetlistConnectionUp
//             && (trans.next === SetlistConnectionDown || isSetlistConnectionErrored(trans.next))
//             && !!this._authtoken
//         ) {
//             this.establishSetlistFeed({
//                 delayFirstAttempt: true,
//                 jitter: "full",
//             });
//         }
//     }
//
//     private _setlistFeedConnectionWatchers: ConnStatTransitionWatcher[] = [
//         trans => {
//             console.log(`setlist connection change ${JSON.stringify(trans)}`);
//         },
//         this.reestablishAfterDropped.bind(this),
//     ];
//
//     private _rawSetlistWatcher?: Watcher<WiredSetlistPacket>;
// }

export const autologinTqk = ["__autologin"];

interface AutologinState {
    wantsAutologin: boolean | undefined;
    storedAuthtoken: string | null | undefined;
}

export interface UseAutologinResult extends AutologinState {
    setWantsAutologin: (wants: boolean) => Promise<unknown>;
}

export const autologinTqo = queryOptions({
    queryKey: autologinTqk,
    queryFn: () => {
        const rv$: Promise<AutologinState> = (import.meta.env.DEV
            ? Promise.all([
                localforage.getItem<boolean>(lfAutologinKey),
                localforage.getItem<string>(lfAuthtokenKey),
            ]).then(([wantsAutologin, storedAuthtoken]) => {
                return {
                    wantsAutologin: !!wantsAutologin,
                    storedAuthtoken,
                };
            })
            : Promise.resolve({wantsAutologin: false, storedAuthtoken: null}));
        return rv$;
    },
    staleTime: Infinity,
});

export function useAuthenticationToken() {
    const auther = useAuthenticator();
    const jwt = useSyncExternalStore(auther.listenJwt, auther.getJwt);
    return jwt;
}

export function useAutologin() {
    const auther = useAuthenticator();
    const jwt = useAuthenticationToken();
    const tqc = useQueryClient();
    const {data} = useQuery(autologinTqo);
    const bomb = useBomb();
    const setWantsAutologin = useCallback((wants: boolean) => {
        const lftasks$: Promise<unknown>[] = [];
        if (wants !== data?.wantsAutologin) {
            if (!wants) {
                lftasks$.push(
                    localforage.removeItem(lfAuthtokenKey),
                    localforage.removeItem(lfAutologinKey));
            } else {
                lftasks$.push(localforage.setItem(lfAutologinKey, true))
            }
        }
        return Promise.all(lftasks$)
            .then(() => tqc.invalidateQueries({queryKey: autologinTqk}));
    }, [data?.wantsAutologin, tqc]);

    useEffect(() => {
        if (!auther.getJwt() && data?.wantsAutologin && data.storedAuthtoken) {
            auther.setJwt(data.storedAuthtoken);
        }
    }, [auther, data?.storedAuthtoken, data?.wantsAutologin]);

    useEffect(() => {
        if (jwt && data?.wantsAutologin) {
            // cannot rely on bomb here
            localforage.setItem(lfAuthtokenKey, auther.getJwt()).catch(bomb);
        }
    }, [jwt, data?.wantsAutologin, auther, bomb]);

    const rv = useMemo<UseAutologinResult>(() => {
        return {
            wantsAutologin: data?.wantsAutologin,
            storedAuthtoken: data?.storedAuthtoken, setWantsAutologin
        }
    }, [data, setWantsAutologin]);

    return rv;
}





